import transitions from './Transitions.module.scss';

import React from 'react';
import { CSSTransition } from 'react-transition-group';
import { scrollToElement } from 'utils/scrollToElement';
import type { AccordionItemId, OpenSections } from '../models';

interface IAccordionBodyProps {
  children: React.ReactNode;
  itemId: AccordionItemId;
  itemContainerClassName?: string;
  openSections: OpenSections;
  className?: string;
  animationModifier?: number;
}

interface IAccordionBodyState {
  isTransitioning: boolean;
}

const initialState: IAccordionBodyState = {
  isTransitioning: false,
};

class AccordionBody extends React.Component<IAccordionBodyProps, IAccordionBodyState> {
  readonly state: IAccordionBodyState = initialState;

  setTransitioning = () => {
    this.setState({ isTransitioning: true });
  };

  unsetTransitioning = () => {
    this.setState({ isTransitioning: false });
  };

  render() {
    const { openSections, itemId, children, className = '', itemContainerClassName } = this.props;
    const { isTransitioning } = this.state;

    const isOpen = !!openSections[itemId];

    return (
      <CSSTransition
        in={isOpen}
        timeout={400}
        classNames={{
          enter: transitions.enter,
          enterDone: transitions.enterDone,
          exit: transitions.exit,
          exitActive: transitions.exitActive,
        }}
        onEnter={this.setTransitioning}
        onEntering={this.useNodeScrollHeight}
        onEntered={this.clearNodeScrollHeight}
        onExit={this.useNodeScrollHeight}
        onExiting={this.clearNodeScrollHeight}
        onExited={this.unsetTransitioning}
      >
        <div
          id={`accordion__body__${itemId}`}
          aria-labelledby={`accordion__header__${itemId}`}
          className={className}
          role="region"
        >
          <div className={itemContainerClassName ?? undefined} hidden={!isOpen && !isTransitioning}>
            {children}
          </div>
        </div>
      </CSSTransition>
    );
  }

  private useNodeScrollHeight = (node: HTMLElement) => {
    const { animationModifier = 1 } = this.props;
    node.style.maxHeight = `${node.scrollHeight * animationModifier}px`;

    scrollToElement(node, 100);
  };

  private clearNodeScrollHeight(node: HTMLElement) {
    node.style.maxHeight = '';
  }
}

export { AccordionBody };
